import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Bot, Zap, Calendar, MessageSquare, Shield, Headphones, Star,
  Stethoscope, Heart, Brain, Baby, Eye, Bone, Activity, Pill
} from "lucide-react";

const AIReceptionistDentalPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Dentists Dallas | Dental Virtual Receptionist | Dental Office Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Appointment Booking", icon: Clock },
    { value: "Reminders", label: "No-Show Reduction", icon: Users },
    { value: "100%", label: "Calls Answered", icon: PhoneCall },
    { value: "After-Hours", label: "New Patient Booking", icon: Calendar }
  ];

  const dentalServices = [
    { icon: Stethoscope, name: "General Dentistry", desc: "Cleanings, exams, fillings, routine care scheduling" },
    { icon: Heart, name: "Cosmetic Dentistry", desc: "Whitening consultations, veneers, smile makeover inquiries" },
    { icon: Brain, name: "Oral Surgery", desc: "Wisdom teeth consultations, implant inquiries, extraction scheduling" },
    { icon: Baby, name: "Pediatric Dentistry", desc: "Kids appointments, first visit scheduling, parent questions" },
    { icon: Bone, name: "Orthodontics", desc: "Invisalign consultations, braces inquiries, adjustment scheduling" },
    { icon: Activity, name: "Emergency Dental", desc: "Urgent toothaches, broken teeth, after-hours dental emergencies" }
  ];

  const features = [
    { 
      icon: Phone, 
      title: "24/7 Appointment Booking", 
      desc: "Patients can schedule cleanings, exams, and procedures any time. Integrates with Dentrix, Eaglesoft, and Open Dental." 
    },
    { 
      icon: Shield, 
      title: "HIPAA Compliant", 
      desc: "Fully compliant with HIPAA regulations. Secure handling of patient information, insurance details, and medical history." 
    },
    { 
      icon: Calendar, 
      title: "Smart Scheduling", 
      desc: "Fills cancellation gaps, books high-value procedures in optimal time slots, and manages multiple hygienists' schedules." 
    },
    { 
      icon: MessageSquare, 
      title: "Insurance Verification", 
      desc: "Collects insurance information, verifies eligibility, and explains coverage to patients before their visit." 
    },
    { 
      icon: Activity, 
      title: "Emergency Triage", 
      desc: "Assesses dental emergencies, provides immediate care instructions, and routes urgent cases to on-call dentists." 
    },
    { 
      icon: Pill, 
      title: "Appointment Reminders", 
      desc: "Automated confirmations, reminders, and follow-ups help reduce no-shows. Handles rescheduling seamlessly." 
    }
  ];

  const comparisons = [
    { item: "Front Desk Staff", traditional: "$38,000-$48,000/year", ai: "$249-$449/month" },
    { item: "After-hours Booking", traditional: "Lost to competitors", ai: "24/7 availability" },
    { item: "No-Show Rate", traditional: "15-25%", ai: "Automated reminders & rescheduling" },
    { item: "Patient Wait Time", traditional: "Hold times vary", ai: "Instant answers" },
    { item: "Insurance Questions", traditional: "Callbacks required", ai: "Immediate verification" },
    { item: "Multi-location Support", traditional: "Staff per location", ai: "One AI, all locations" }
  ];

  const faqs = [
    { 
      q: "How does AI handle dental appointment scheduling?", 
      a: "Our dental AI receptionist integrates directly with your practice management software (Dentrix, Eaglesoft, Open Dental, etc.). It sees real-time availability, understands procedure durations, matches patients with appropriate providers, and books appointments instantly. It even considers your scheduling preferences—like booking longer procedures in mornings or clustering hygiene appointments."
    },
    { 
      q: "Is the AI receptionist HIPAA compliant for dental offices?",
      a: "Absolutely. Our AI is fully HIPAA compliant with encrypted communications, secure data handling, and proper PHI protocols. We sign Business Associate Agreements (BAAs), maintain audit trails, and follow all required safeguards. Your patient information is protected at the same level as major healthcare systems."
    },
    { 
      q: "What happens when a patient has a dental emergency after hours?",
      a: "The AI immediately assesses the urgency—severe pain, trauma, uncontrolled bleeding, or infection symptoms. It provides appropriate first aid guidance, captures the patient's information and symptoms, and alerts your on-call dentist with full details. For true emergencies, it can direct patients to emergency dental clinics or ERs while notifying your team."
    },
    { 
      q: "Can AI really reduce patient no-shows?",
      a: "Yes, significantly. Our AI sends personalized appointment reminders via text, email, and phone at optimal intervals. It makes rescheduling easy—if a patient can't make it, the AI immediately offers alternatives and fills the gap. Practices see fewer no-shows because patients receive the right reminders and can easily adjust without calling during business hours."
    },
    { 
      q: "How does it handle insurance and payment questions?",
      a: "The AI collects insurance information, explains your accepted plans, and can perform basic eligibility verification. It answers common questions about financing options, payment plans, and procedure costs. For complex insurance scenarios, it captures details and schedules a callback from your billing team with full context."
    },
    { 
      q: "Will patients feel comfortable talking to AI about dental concerns?",
      a: "Modern dental AI sounds natural and empathetic—patients often don't realize they're speaking with AI. More importantly, patients love instant access to scheduling and answers, especially for routine matters. For patients who prefer human contact, the AI seamlessly transfers to your team. Studies show higher patient satisfaction when calls are answered immediately versus going to voicemail."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Dental Practices",
        "description": "24/7 AI-powered virtual receptionist for dental offices in Dallas-Fort Worth. HIPAA-compliant appointment scheduling, patient intake, insurance verification, and emergency triage.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "3333 Harry Hines Blvd",
            "addressLocality": "Dallas",
            "addressRegion": "TX",
            "postalCode": "75201"
          }
        },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney", "Irving"],
        "serviceType": "AI Virtual Receptionist for Dentists"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Dental Receptionist Service",
        "description": "24/7 AI receptionist for dental practices with HIPAA compliance",
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "249",
          "highPrice": "449",
          "priceCurrency": "USD"
        },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-dental-page">
      <Helmet>
        <title>AI Receptionist for Dentists Dallas | Dental Virtual Receptionist | HIPAA Compliant Answering Service</title>
        <meta name="description" content="AI receptionist for Dallas dental practices. 24/7 appointment scheduling, HIPAA compliant patient intake, insurance verification & emergency triage. Reduce no-shows. Free demo!" />
        <meta name="keywords" content="AI receptionist dentist Dallas, dental virtual receptionist, dental office answering service DFW, HIPAA compliant AI receptionist, dental appointment booking AI, dentist phone answering service, dental practice automation" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-dentists" />
        <meta property="og:title" content="AI Receptionist for Dentists Dallas | 24/7 HIPAA Compliant" />
        <meta property="og:description" content="Never miss a patient call. AI receptionist handles dental scheduling 24/7. HIPAA compliant, integrates with Dentrix & Eaglesoft." />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 via-transparent to-cyan-900/10"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full">
                <Stethoscope className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">HIPAA Compliant • Dental-Specific AI</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-cyan-400">Dental Practices</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Fill your schedule 24/7 with AI that handles appointments, answers patient questions, and reduces no-shows with automated reminders. HIPAA compliant, integrates with your practice management software.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="healthcare"
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-cyan-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center">
                      <Stethoscope className="w-8 h-8 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Dental Practice AI</h3>
                      <p className="text-cyan-400">Built for Modern Dentistry</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Dentrix, Eaglesoft & Open Dental integration", "HIPAA compliant patient handling", "Smart scheduling with gap filling", "Automated appointment reminders"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dental Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Dental Services We Support</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">AI trained for every aspect of modern dental practice.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dentalServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-cyan-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-cyan-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Dental Practices</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Dental Reception Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional Staff</th>
                  <th className="text-center py-4 px-6 text-cyan-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-cyan-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-cyan-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-cyan-500/10 to-cyan-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for Dental Practices
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your dental practice—from HIPAA compliance
                  to integrating with Dentrix and Eaglesoft, plus ROI calculations and no-show reduction strategies.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    HIPAA compliance guide
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    Dental software integrations
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    No-show reduction strategies
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-dentists-dental-practices">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-cyan-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Stethoscope className="w-8 h-8 text-cyan-400" />
                    <div>
                      <div className="text-white font-semibold">15 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ Dental Specialty Features</div>
                    <div>✓ Insurance Handling Setup</div>
                    <div>✓ Emergency Triage Protocols</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-600/20 to-cyan-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Fill Your Schedule 24/7?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">
            Join Dallas dental practices using AI to book more patients and reduce no-shows.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-8">
              Schedule Your Free Demo
            </BookConsultationButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistDentalPage;
